import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputHeader } from '../Headers/styles'
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk'

export interface Option {
  label: string
  value: string | number
  disabled?: boolean
}

type FormSelectProps = TextFieldProps & {
  name: string
  options: Option[]
  allOptions?: Option[]
  placeholder?: string
  actionLabel?: React.ReactNode
  allowClear?: boolean
  disableLabel?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  loading?: boolean
}

function useMaxLabelLength(ref: React.RefObject<HTMLElement>, font = '16px Roboto') {
  const [maxLength, setMaxLength] = useState(55)

  useEffect(() => {
    function recalculate() {
      if (ref.current) {
        const width = ref.current.offsetWidth
        const SCALING_FACTOR = width < 390 ? 1.08 : width > 500 ? 1.15 : 1.17

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (context) {
          context.font = font
          const sample = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
          const sampleWidth = context.measureText(sample).width
          const avgCharWidth = sampleWidth / sample.length
          const estimatedLength = Math.floor((width / avgCharWidth) * SCALING_FACTOR)

          setMaxLength(Math.max(5, Math.min(estimatedLength - 2, 250)))
        }
      }
    }

    recalculate()
    window.addEventListener('resize', recalculate)
    return () => window.removeEventListener('resize', recalculate)
  }, [ref, font])

  return maxLength
}

const FormSelect: React.FC<FormSelectProps> = ({
  name,
  options,
  allOptions,
  label,
  placeholder = 'Please select',
  disableLabel = 'No Items',
  allowClear = false,
  actionLabel,
  onChange,
  loading = false,
  ...props
}) => {
  const { control } = useFormContext()

  const labelBoxRef = useRef<HTMLDivElement>(null)
  const MAX_LABEL_LENGTH = useMaxLabelLength(labelBoxRef, '16px Roboto')

  const combinedOptions = React.useMemo(() => {
    if (allowClear) {
      return [{ label: 'None', value: '', disabled: false }, ...options]
    }
    return options
  }, [allowClear, options])

  const TruncateWithTooltip: React.FC<{ children: string; tooltip?: string }> = ({
    children,
    tooltip,
  }) => {
    const shouldTruncate = children.length > MAX_LABEL_LENGTH
    const displayText = shouldTruncate ? children.slice(0, MAX_LABEL_LENGTH - 1) + '…' : children

    const textNode = (
      <Typography
        sx={{
          maxWidth: 340,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
        component='span'
      >
        {displayText}
      </Typography>
    )

    return shouldTruncate ? (
      <Tooltip title={tooltip || children} placement='top'>
        {textNode}
      </Tooltip>
    ) : (
      textNode
    )
  }

  return (
    <Box mb={2} ref={labelBoxRef}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <InputHeader gutterBottom>
          <Box component='span' display='inline-flex' alignItems='baseline'>
            {label}
            <Box component='span' visibility={props.required ? 'visible' : 'hidden'} ml={0.5}>
              <RequiredAsterisk />
            </Box>
          </Box>
        </InputHeader>
        {actionLabel && actionLabel}
      </Box>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            {...props}
            sx={{
              ...(props.sx || {}),
              '& .MuiInputBase-root': {
                backgroundColor: '#FFFFFF',
                height: '43px',
                minHeight: '43px',
              },
              '& .MuiSelect-select': {
                paddingTop: '10px',
                paddingBottom: '10px',
                height: '23px',
                minHeight: '23px',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiSelect-icon': {
                right: loading ? '36px' : '8px',
              },
              '& .MuiOutlinedInput-input': {
                paddingTop: '10px',
                paddingBottom: '10px',
              },
            }}
            placeholder={props.disabled ? '' : (options.length === 0 ? disableLabel : placeholder)}
            value={field.value ?? ''}
            onChange={(event) => {
              const value = event.target.value === '' ? null : event.target.value
              if (onChange) {
                onChange(event)
              }
              field.onChange(value)
            }}
            select
            disabled={props.disabled || options.length === 0 || loading}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              },
              renderValue: (value: unknown) => {
                if (!value) {
                  if (props.disabled) {
                    return null
                  }
                  return (
                    <Box component="span" sx={{ color: 'text.primary', opacity: 0.38 }}>
                      {options.length === 0 ? disableLabel : placeholder}
                    </Box>
                  )
                }
                let selectedOption = combinedOptions.find((option) => option.value == value)
                if (!selectedOption && allOptions) {
                  selectedOption = allOptions.find((option) => option.value == value)
                }
                const label = selectedOption ? selectedOption.label : value
                if (!label) {
                  return null
                }
                const labelString = typeof label === 'string' ? label : String(label)
                return (
                  <TruncateWithTooltip tooltip={labelString}>{labelString}</TruncateWithTooltip>
                )
              },
              IconComponent: (iconProps) => (
                <>
                  {loading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress size={20} thickness={5} />
                    </Box>
                  )}
                  <span {...iconProps} />
                </>
              ),
            }}
            fullWidth
            helperText={error ? error.message : ''}
            error={!!error}
            variant='outlined'
          >
            {combinedOptions.map((option) => {
              const shouldTruncate = option.label.length > MAX_LABEL_LENGTH
              const displayText = shouldTruncate
                ? option.label.slice(0, MAX_LABEL_LENGTH - 1) + '…'
                : option.label
              return (
                <MenuItem key={option.value ?? -1} value={option.value} disabled={option.disabled}>
                  <Box
                    sx={{
                      maxWidth: 340,
                      display: 'block',
                    }}
                    title={shouldTruncate ? option.label : undefined}
                  >
                    {displayText}
                  </Box>
                </MenuItem>
              )
            })}
          </TextField>
        )}
      />
    </Box>
  )
}

export default FormSelect
